/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onMounted,
  ref,
  shallowRef,
  triggerRef,
} from "vue";
import { LemmonGrid, type GridSchema } from "@lemmonhq/uikit";
import { Loading } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { AddCategoryModal } from "../components/AddCategoryModal";
import type { Category } from "@lemmonhq/types";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

const buildSchema = (colorPresets: string[]): GridSchema<Category> => ({
  primaryKey: "id",
  columns: [
    {
      key: "id",
      header: "ID",
      type: "readonly",
      width: 72,
      frozen: true,
      cellClass: "font-mono text-xs text-base-content/50",
    },
    {
      key: "name",
      header: "Name",
      type: "text",
      width: 240,
      frozen: true,
      editable: true,
      bulkEditable: true,
      required: true,
      placeholder: "Category name",
      cellClass: "font-semibold !text-accent",
    },
    {
      key: "status",
      header: "Status",
      type: "select",
      width: 130,
      editable: true,
      bulkEditable: true,
      options: [
        { value: "active", label: "Active", icon: "check_circle" },
        { value: "inactive", label: "Inactive", icon: "cancel" },
      ],
    },
    {
      key: "order",
      header: "Order",
      type: "number",
      width: 90,
      editable: true,
      bulkEditable: true,
      min: 0,
      step: 1,
    },
    {
      key: "color",
      header: "Color",
      type: "color",
      width: 130,
      editable: true,
      colorPresets,
    },
  ],
});

export const CategoriesSubPage = defineComponent({
  name: "CategoriesSubPage",
  setup() {
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const error = ref<string | null>(null);
    const data = shallowRef<Category[]>([]);
    const changedRowIds = ref<Set<number | string>>(new Set());
    const saving = ref(false);
    const showAddCategory = ref(false);

    const hasChanges = computed(() => changedRowIds.value.size > 0);

    // Categories are the top of the hierarchy — presets are just other
    // categories' colors already in use, no parent level to pull from.
    const schema = computed(() => {
      const colorPresets = Array.from(
        new Set(
          data.value.map((c) => c.color).filter((c): c is string => Boolean(c)),
        ),
      );
      return buildSchema(colorPresets);
    });

    const loadCategories = async () => {
      const categories = await trpc.categories.list.query();
      data.value = categories as Category[];
    };

    onMounted(async () => {
      try {
        await loadCategories();
      } catch (e) {
        error.value = "Failed to load categories";
      } finally {
        loading.value = false;
      }
    });

    const handleCellChange = (
      rowId: string | number,
      field: string,
      value: any,
    ) => {
      const row = data.value.find((r) => r.id === rowId);
      if (!row) return;
      (row as any)[field] = value;
      changedRowIds.value = new Set([...changedRowIds.value, rowId]);
      triggerRef(data);
    };

    const handleBulkChange = (
      rowIds: Set<string | number>,
      field: string,
      value: any,
    ) => {
      for (const id of rowIds) handleCellChange(id, field, value);
    };

    const handleSave = async () => {
      saving.value = true;
      try {
        const changed = Array.from(changedRowIds.value)
          .map((id) => data.value.find((r) => r.id === id))
          .filter(Boolean) as Category[];

        await Promise.all(
          changed.map((category) =>
            trpc.categories.update.mutate({
              id: category.id,
              name: category.name,
              status: category.status,
              order: category.order,
              color: category.color ?? null,
            }),
          ),
        );

        changedRowIds.value = new Set();
        await loadCategories();
        toast.showToast("Categories saved", { type: "success" });
      } catch (e) {
        toast.showToast("Failed to save categories", { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const handleDiscard = async () => {
      loading.value = true;
      try {
        await loadCategories();
        changedRowIds.value = new Set();
      } finally {
        loading.value = false;
      }
    };

    const handleDeleteRows = async (rowIds: Set<string | number>) => {
      if (rowIds.size === 0) return;
      if (
        !confirm(
          rowIds.size === 1
            ? "Delete this category?"
            : `Delete ${rowIds.size} categories?`,
        )
      ) {
        return;
      }
      try {
        await Promise.all(
          Array.from(rowIds).map((id) =>
            trpc.categories.delete.mutate({ id: id as number }),
          ),
        );
        await loadCategories();
        toast.showToast("Categories deleted", { type: "success" });
      } catch (e) {
        toast.showToast("Failed to delete categories", { type: "error" });
      }
    };

    const handleAddCategory = async (name: string) => {
      try {
        await trpc.categories.create.mutate({ name });
        showAddCategory.value = false;
        await loadCategories();
        toast.showToast("Category created", { type: "success" });
      } catch (e) {
        toast.showToast("Failed to create category", { type: "error" });
      }
    };

    return () => {
      if (loading.value) {
        return (
          <div class="flex h-full w-full items-center justify-center">
            <Loading />
          </div>
        );
      }

      if (error.value) {
        return (
          <div class="flex h-full w-full items-center justify-center text-error">
            {error.value}
          </div>
        );
      }

      return (
        <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
          {/* Header */}
          <SubpageHeader
            title="Categories"
            subtitle="Double-click or press Enter to edit · Tab / arrows to navigate"
            v-slots={{
              badges: () => (
                <>
                  <span class="rounded-md border border-base-300 bg-base-100 px-2 py-0.5 text-xs font-medium text-base-content/60">
                    {data.value.length}
                  </span>
                  {hasChanges.value && (
                    <span class="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {changedRowIds.value.size} changed
                    </span>
                  )}
                </>
              ),
              default: () => (
                <>
                  <div class="flex items-center gap-2">
                    <button
                      class="btn btn-neutral btn-sm"
                      onClick={() => (showAddCategory.value = true)}
                    >
                      <span class="material-symbols-outlined text-base">
                        add
                      </span>
                      Add Category
                    </button>
                    {hasChanges.value && (
                      <>
                        <button
                          class="btn btn-ghost btn-sm text-base-content/70"
                          onClick={handleDiscard}
                        >
                          <span class="material-symbols-outlined text-base">
                            undo
                          </span>
                          Discard
                        </button>
                        <button
                          class="btn btn-primary btn-sm"
                          disabled={saving.value}
                          onClick={handleSave}
                        >
                          <span class="material-symbols-outlined text-base">
                            save
                          </span>
                          {saving.value ? "Saving…" : "Save changes"}
                        </button>
                      </>
                    )}
                  </div>
                </>
              ),
            }}
          />

          {/* Grid */}
          <div class="flex-1 overflow-hidden p-3">
            <LemmonGrid
              data={data.value}
              schema={schema.value}
              changedRowIds={changedRowIds.value}
              onCellChange={handleCellChange}
              onBulkChange={handleBulkChange}
              onDeleteRows={handleDeleteRows}
            />
          </div>

          <AddCategoryModal
            isOpen={showAddCategory.value}
            onClose={() => (showAddCategory.value = false)}
            onAdd={handleAddCategory}
          />
        </div>
      );
    };
  },
});
